import React, {Component} from "react";
import PropTypes from "prop-types";
import {connect} from "react-redux";
import {Button, Spin} from "antd";
import {bindActionCreators} from "redux";
import {getCategoryTree} from "./actions";
import axios from "axios";
import {authorizationHeaders} from "../utils/api";
import MoneyDisplay from "../common/MoneyDisplay";
import "./InventoryByCategoryReportPage.css";
import {defaultComparer} from "../utils/compare";
import SelectCategoryTree from "./SelectCategoryTree";
import {getCategoryAndChildrenIds} from "./tree";

class InventoryByCategoryReportPage extends Component {
  static propTypes = {
    // state
    salonId: PropTypes.string.isRequired,
    loading: PropTypes.bool.isRequired,
    categoryTree: PropTypes.object.isRequired,
    // dispatch
    getCategoryTree: PropTypes.func.isRequired,
  };

  state = {
    categoryId: null,
    data: [],
  };

  async componentDidMount() {
    await this._loadCategories();
  }

  async componentDidUpdate(prevProps) {
    if (this.props.salonId !== prevProps.salonId) await this._loadCategories();
  }

  async _loadCategories() {
    try {
      await this.props.getCategoryTree({salonId: this.props.salonId});
      this.setState({categoryId: null, data: []});
    } catch (err) {
      console.error(err);
    }
  }

  async _loadData(categoryId) {
    if (categoryId === null) return this.setState({data: []});

    const filter = {
      where: {
        salonId: {eq: this.props.salonId},
        quantityInStock: {gt: 0},
      },
      limit: 10000,
    };
    if (categoryId) {
      const categoryIds = getCategoryAndChildrenIds(this.props.categoryTree, categoryId);
      filter.where.categoryId = {inq: categoryIds};
    }

    try {
      const res = await axios.get(
        `${process.env.PUBLIC_URL}/api/v1/r/inventoryByCategoryReport?filter=${encodeURIComponent(JSON.stringify(filter))}&_=${Date.now()}`,
        {headers: authorizationHeaders()}
      );
      this.setState({data: res.data});
    } catch (err) {
      console.error(err);
    }
  }

  _onCategoryChange = async(categoryId) => {
    this.setState({categoryId});
    await this._loadData(categoryId);
  };

  _handlePrint = () => window.print();

  render() {
    const {categoryTree, loading} = this.props;
    const {categoryId, data} = this.state;
    const report = data.reduce(
      (
        {categories, grandTotalAmountForSale, grandTotalCost},
        {categoryId, categoryName, display, quantityInStock, salePrice, costPrice}
      ) => {
        const category = categories.find(c => c.categoryId === categoryId) || (categories[categories.length] = {
          categoryId, categoryName, products: [], totalAmountForSale: 0, totalCost : 0,
        });
        const amountForSale = quantityInStock * salePrice;
        category.totalAmountForSale += amountForSale;
        grandTotalAmountForSale += amountForSale;
        const totalCost = quantityInStock * costPrice;
        category.totalCost += totalCost;
        grandTotalCost += totalCost;
        category.products.push({display, quantityInStock, salePrice, costPrice, amountForSale, totalCost});
        return {categories, grandTotalAmountForSale, grandTotalCost};
      },
      {categories: [], grandTotalAmountForSale: 0, grandTotalCost: 0}
    );
    const sortedCategories = report.categories.sort(({name: a}, {name: b}) => defaultComparer(a, b)).map(category => ({
      ...category,
      products: category.products.sort(({display: a}, {display: b}) => defaultComparer(a, b)),
    }));
    return (
      <div style={{padding: '1em'}}>
        <div className="print-hide" style={{display: 'flex', flex: '1', alignItems: 'center'}}>
          <div>Category&nbsp;</div>
          <div style={{flex: '1'}}>
            <SelectCategoryTree
              tree={categoryTree}
              showAll
              value={categoryId}
              onChange={this._onCategoryChange}
              placeholder="Select category"
              style={{width: '300px'}}
            />
          </div>
          <Button icon="printer" onClick={this._handlePrint}>Print</Button>
        </div>
        <Spin spinning={loading}>
          <h1>Inventory by Category</h1>
          <table id="inventoryByCategoryReport">
            <tbody>
              {sortedCategories.map(({categoryName, products, totalAmountForSale, totalCost}, index) =>
                <React.Fragment key={index}>
                  <tr className="inventoryByCategoryReport-category-header">
                    <td>Category</td>
                    <td>{categoryName}</td>
                    <td colSpan={4}>&nbsp;</td>
                  </tr>
                  <tr><td colSpan={6}>&nbsp;</td></tr>
                  <tr className="inventoryByCategoryReport-product-header">
                    <td>Display</td>
                    <td>Quantity In Stock</td>
                    <td>Sale Price</td>
                    <td>Amount for Sale</td>
                    <td>Cost Price</td>
                    <td>Total Cost</td>
                  </tr>
                  {products.map(({display, quantityInStock, salePrice, costPrice, amountForSale, totalCost}, index) =>
                    <tr key={index} className="inventoryByCategoryReport-product-body">
                      <td>{display}</td>
                      <td>{quantityInStock}</td>
                      <td><MoneyDisplay amount={salePrice}/></td>
                      <td><MoneyDisplay amount={amountForSale}/></td>
                      <td><MoneyDisplay amount={costPrice}/></td>
                      <td><MoneyDisplay amount={totalCost}/></td>
                    </tr>
                  )}
                  <tr><td colSpan={6}>&nbsp;</td></tr>
                  <tr className="inventoryByCategoryReport-category-footer">
                    <td>Total</td>
                    <td>{categoryName}</td>
                    <td colSpan={2}><MoneyDisplay amount={totalAmountForSale} /></td>
                    <td colSpan={2}><MoneyDisplay amount={totalCost} /></td>
                  </tr>
                  <tr><td colSpan={6}>&nbsp;</td></tr>
                </React.Fragment>
              )}
              <tr className="inventoryByCategoryReport-total-header">
                <td colSpan={4}>Amount for Sale</td>
                <td colSpan={2}>Total Cost</td>
              </tr>
              <tr className="inventoryByCategoryReport-total-body">
                <td colSpan={2}>Grand Total</td>
                <td colSpan={2}><MoneyDisplay amount={report.grandTotalAmountForSale}/></td>
                <td colSpan={2}><MoneyDisplay amount={report.grandTotalCost}/></td>
              </tr>
            </tbody>
          </table>
        </Spin>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  salonId: state.salon.selectedSalonId,
  loading: state.category.categoriesLoading,
  categoryTree: state.category.categoryTree,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  getCategoryTree,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(InventoryByCategoryReportPage);



// WEBPACK FOOTER //
// ./src/Category/InventoryByCategoryReportPage.js